import {
  StrictMode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { createRoot } from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./styles.css";
import { JiraImportPanel } from "./jira-import-panel";
import { RichTextEditor, RichTextView, type Member } from "./rich-text-editor";
import { docToPlainText, type TiptapDoc } from "./rich-text-doc";
import {
  portalRoutePath,
  readPortalRoute,
  readSharedTaskKey,
  type PortalRoute,
  type PortalView,
} from "./portal-routes";
import { TaskWorkspace } from "./task-workspace";
import { TaskHierarchy } from "./task-hierarchy";
import { SprintsPage } from "./sprints-page";
import { GoalsPage } from "./goals-page";
import { NotificationBell } from "./notification-bell";
import { OnboardingPage } from "./onboarding-page";
import { AgentTokensDialog } from "./agent-tokens-panel";
import { ReportsPage } from "./reports-page";
import { ISSUE_TYPES, issueTypeMeta } from "./task-icons";
import { ConfluenceImportPanel } from "./confluence-import-panel";
import {
  AvatarContent,
  ConfirmProvider,
  ErrorBanner,
  ErrorBoundary,
  RequiredMark,
  Skeleton,
  ToastProvider,
  useConfirm,
  useModalA11y,
  useTheme,
  useToast,
} from "./ui-kit";
import { parseServerTime } from "./server-time";

type PortalConfig = {
  appName: string;
  environment: string;
  googleAuthConfigured: boolean;
};

type User = {
  avatarUrl: string | null;
  displayName: string;
  email: string;
  id: string;
};

type Session =
  | { authenticated: false }
  | { authenticated: true; user: User };

type Team = {
  id: string;
  name: string;
  role: "admin" | "member";
  slug: string;
  taskPermissions: TaskPermission[];
};

type TemplateSection = {
  color: string | null;
  id: string;
  position: number;
  prompt: string | null;
  templateId: string;
  title: string;
};

type RetroTemplate = {
  description: string | null;
  id: string;
  isDefault: boolean;
  name: string;
  sections: TemplateSection[];
  status: "active" | "archived";
  teamId: string | null;
};

type RetroSummary = {
  actionCount: number;
  cardCount: number;
  completedActionCount: number;
  createdAt: string;
  facilitatorId: string;
  facilitatorName: string;
  id: string;
  overdueActionCount: number;
  sprintLabel: string | null;
  stage: string;
  templateName: string | null;
  title: string;
};

type ActionItem = {
  description: string | null;
  dueOn: string | null;
  facilitatorId?: string | null;
  id: string;
  isCarriedOver?: number;
  isOverdue?: number;
  // Present once the backend query was updated to expose it (see
  // portal-api.ts's listActions / retrospectiveDetail queries). A row with
  // itemType other than "action" has already been created directly as a
  // real backlog task (see createTaskFromRetroAction) -- ActionRow renders
  // those as a simplified, read-only pointer into the backlog instead of
  // the full action-item controls (status toggle / push / delete), since
  // those endpoints no longer act on non-action rows.
  itemType?: string;
  ownerAvatarUrl: string | null;
  ownerId: string;
  ownerName: string;
  retrospectiveId?: string;
  retrospectiveTitle?: string;
  status: "open" | "in_progress" | "done" | "cancelled";
  taskKey?: string | null;
  title: string;
};

type Workspace = {
  actions: ActionItem[];
  members: User[];
  metrics: {
    actionCompletionRate: number;
    closedRetrospectives: number;
    completedActions: number;
    overdueActions: number;
    totalActions: number;
    totalRetrospectives: number;
  };
  retrospectives: RetroSummary[];
  team: Team;
  teams: Team[];
  templates: RetroTemplate[];
  user: User;
};

type RetroSection = {
  color: string | null;
  id: string;
  position: number;
  prompt: string | null;
  title: string;
};

type RetroCard = {
  authorAvatarUrl: string | null;
  authorId: string;
  authorName: string;
  body: TiptapDoc;
  createdAt: string;
  id: string;
  sectionId: string;
};

type ActionSuggestion = {
  authorId: string;
  authorName: string;
  createdAt: string;
  createdWorkItemId: string | null;
  description: string;
  id: string;
  rationale: string;
  sourceCardIds: string[];
  status: "pending" | "accepted" | "rejected";
  successMetric: string;
  suggestedDueOn: string;
  targetValue: string;
  title: string;
};

type RetroDetail = {
  actions: ActionItem[];
  canGenerateActionSuggestions: boolean;
  canManageCards: boolean;
  canControlStage: boolean;
  cards: RetroCard[];
  hasFeedback: boolean;
  members: User[];
  retrospective: {
    createdAt: string;
    facilitatorId: string;
    id: string;
    isReadOnly: number;
    sprintLabel: string | null;
    stage: string;
    teamId: string;
    title: string;
  };
  sections: RetroSection[];
  sprints: Array<{ id: string; name: string; state: "active" | "planned" }>;
  suggestionGenerationInProgress: boolean;
  suggestions: ActionSuggestion[];
  summary: {
    actionCount: number;
    cardCount: number;
    completedActionCount: number;
    overdueActionCount: number;
  };
};

type AdminMember = User & {
  isPortalAdmin: number;
  joinedAt: string;
  role: "admin" | "member";
  taskPermissions: TaskPermission[];
};

type TaskPermission =
  | "manage_tasks"
  | "manage_board"
  | "manage_sprints"
  | "manage_integrations"
  | "view_task_reports";

const TASK_PERMISSION_DETAILS: Record<TaskPermission, { description: string; label: string }> = {
  manage_tasks: { label: "Manage tasks", description: "Edit or delete team tasks, moderate comments, and manage relationships." },
  manage_board: { label: "Configure board", description: "Create and edit columns, WIP limits, ordering, and delivery mode." },
  manage_sprints: { label: "Manage sprints", description: "Create, start, complete, reopen, delete, and configure sprint capacity." },
  manage_integrations: { label: "Manage integrations", description: "Connect external systems, run imports, and delete import history." },
  view_task_reports: { label: "View task reports", description: "Open team delivery and task reporting views." },
};

type TeamInvitation = {
  createdAt: string;
  email: string;
  id: string;
  role: "admin" | "member";
  status: "pending";
};

type AdminOverview = {
  aiSettings: {
    connectionCheckedAt: string | null;
    connectionErrorCode: string | null;
    connectionLatencyMs: number | null;
    connectionMessage: string | null;
    connectionStatus: "not_tested" | "operational" | "failed";
    model: "gpt-5.6-luna" | "gpt-5.6-sol" | "gpt-5.6-terra";
    prompt: string;
    reasoningEffort: "low" | "medium" | "high";
    refineModel: "gpt-5.6-luna" | "gpt-5.6-sol" | "gpt-5.6-terra";
    refinePrompt: string;
    refineReasoningEffort: "low" | "medium" | "high";
    updatedAt: string;
  };
  invitations: TeamInvitation[];
  members: AdminMember[];
  templates: RetroTemplate[];
  viewerIsPortalAdmin: boolean;
};

type BranchCleanupSettings = {
  enabled: boolean;
  liveState?: "DISABLED" | "ENABLED" | "UNKNOWN";
  ruleName: string;
  updatedAt: string | null;
  updatedBy: string | null;
};

type OpenAiBilling = {
  adminKeyConfigured: boolean;
  billingUrl: string;
  currency: "usd";
  monthToDateCost: number | null;
  periodEndsAt: string;
  periodStartsAt: string;
};

const api = async <T,>(
  path: string,
  init?: RequestInit,
): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body
      ? { "content-type": "application/json", ...init.headers }
      : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) {
    throw new Error(payload.message || "Taskei could not complete that request.");
  }
  return payload;
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("en-CA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parseServerTime(value));

const templateInitials = (name: string) => {
  const words = name.split(/[\s,]+/).filter(Boolean);
  return (
    words.length === 1
      ? name.slice(0, 2)
      : words.map((word) => word[0]).join("")
  )
    .slice(0, 3)
    .toUpperCase();
};

function LogoMark() {
  return (
    <span aria-hidden="true" className="logo-mark">
      <i className="bi bi-kanban-fill" />
    </span>
  );
}

// `allNames` is the roster this avatar is shown among -- pass it wherever two
// teammates could collide on a first initial (member lists, retro cards, action
// owners) so AvatarContent can escalate "A" -> "Al"/"Am" etc. Omitted only for
// the signed-in user's own nav avatar, which stands alone.
function Avatar({ allNames, user }: {
  allNames?: string[];
  user: Pick<User, "avatarUrl" | "displayName">;
}) {
  return (
    <span className="avatar" aria-hidden={user.avatarUrl ? undefined : "true"}>
      <AvatarContent allNames={allNames} avatarUrl={user.avatarUrl} name={user.displayName} />
    </span>
  );
}

function NoteComposer({
  ariaLabel,
  busy,
  disabled,
  members,
  onChange,
  onSubmit,
  value,
}: {
  ariaLabel: string;
  busy: boolean;
  disabled: boolean;
  members: Member[];
  onChange: (value: TiptapDoc) => void;
  onSubmit: () => void;
  value: TiptapDoc | null;
}) {
  return (
    <div className="note-composer">
      <RichTextEditor
        ariaLabel={ariaLabel}
        characterLimit={1000}
        disabled={disabled}
        members={members}
        onChange={onChange}
        onSubmit={onSubmit}
        placeholder="Add your thought — type @ to mention someone"
        submitOnEnter
        value={value}
      />
      <div className="note-composer-footer">
        <small>Enter to add · Shift+Enter for a new line</small>
        <button
          className="add-note"
          disabled={disabled || busy || !docToPlainText(value).trim()}
          onClick={onSubmit}
          type="button"
        >
          {busy ? "Adding…" : "+ Add note"}
        </button>
      </div>
    </div>
  );
}

function Landing({
  config,
  error,
  returnTo,
}: {
  config: PortalConfig | null;
  error: string | null;
  returnTo?: string;
}) {
  const authReady = Boolean(config?.googleAuthConfigured);
  const signInHref = returnTo ? `/auth/google?returnTo=${encodeURIComponent(returnTo)}` : "/auth/google";
  const { theme, toggleTheme } = useTheme();
  return (
    <main className="landing">
      <nav className="landing-nav">
        <a className="brand" href="/">
          <LogoMark />
          <span>Taskei</span>
        </a>
        <span className="landing-nav-actions">
          {config && config.environment !== "Production" && (
            <span className="environment-pill">
              <span className="status-dot" />
              {config.environment}
            </span>
          )}
          <button
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className="chrome-icon-button"
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            type="button"
          >
            <i aria-hidden="true" className={`bi ${theme === "dark" ? "bi-sun" : "bi-moon-stars"}`} />
          </button>
        </span>
      </nav>
      <section className="hero">
        <div>
          <p className="eyebrow">The engineering workspace for Novixpert</p>
          <h1>
            Plan the sprint.
            <br />
            <span>Reflect when it’s done.</span>
          </h1>
          <p className="hero-description">
            Plan and track work on a Jira-style board, import from Jira and
            Confluence, then run focused retrospectives that turn insight into
            owned action.
          </p>
          <a
            aria-disabled={!authReady}
            className={`google-button ${authReady ? "" : "is-disabled"}`}
            href={authReady ? signInHref : "#"}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.4Z" />
              <path fill="#34A853" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2.1 1-3.4 1-2.6 0-4.8-1.8-5.6-4.2H3v2.6A10 10 0 0 0 12 22Z" />
              <path fill="#FBBC05" d="M6.4 13.9A6 6 0 0 1 6.1 12c0-.7.1-1.3.3-1.9V7.5H3A10 10 0 0 0 2 12c0 1.6.4 3.2 1 4.5l3.4-2.6Z" />
              <path fill="#EA4335" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.8A9.6 9.6 0 0 0 12 2a10 10 0 0 0-9 5.5l3.4 2.6C7.2 7.7 9.4 5.9 12 5.9Z" />
            </svg>
            Continue with Google Workspace
          </a>
          <p className="auth-note">
            {error ??
              (authReady
                ? "Restricted to verified @novixpert.com accounts."
                : "Google Workspace authentication is not configured.")}
          </p>
        </div>
        <aside className="landing-preview">
          <p className="mini-label">Platform · Sprint 12</p>
          <div className="preview-title">
            <h2>Board &amp; retrospective</h2>
            <span>In progress</span>
          </div>
          <div className="preview-columns">
            <div>
              <strong>Sprint board</strong>
              <p>Import the backlog from Jira</p>
              <p>Ship the release checklist</p>
            </div>
            <div>
              <strong>Retrospective</strong>
              <p>Pairing made rollout decisions much faster.</p>
              <p>Smaller pull requests reduced review time.</p>
            </div>
          </div>
        </aside>
      </section>
      <footer className="landing-footer">
        <p>
          Can't sign in, or don't have access yet? Ask your Novixpert
          workspace administrator to add your <strong>@novixpert.com</strong>{" "}
          Google account to Taskei.
        </p>
      </footer>
    </main>
  );
}

function NewRetroDialog({
  team,
  templates,
  onClose,
  onCreated,
}: {
  team: Team;
  templates: RetroTemplate[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [templateId, setTemplateId] = useState(
    templates.find((template) => template.isDefault)?.id ?? templates[0]?.id,
  );
  const selectedTemplate = templates.find(
    (template) => template.id === templateId,
  );

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setError("");
    try {
      const result = await api<{ id: string }>("/api/retrospectives", {
        method: "POST",
        body: JSON.stringify({
          sprintLabel: form.get("sprintLabel"),
          teamId: team.id,
          templateId,
          title: form.get("title"),
        }),
      });
      onCreated(result.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create retrospective.");
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        aria-labelledby="new-retro-title"
        aria-modal="true"
        className="dialog"
        onMouseDown={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
      >
        <button className="dialog-close" onClick={onClose} type="button">
          ×
        </button>
        <p className="eyebrow">Retrospective format</p>
        <h2 id="new-retro-title">Start a retrospective</h2>
        <p>Choose a built-in format or a custom template created by your administrator.</p>
        <form onSubmit={submit}>
          <label>
            <span>Retrospective title<RequiredMark /></span>
            <input autoFocus name="title" placeholder="Sprint 14 retrospective" required />
          </label>
          <label>
            Sprint or cycle
            <input name="sprintLabel" placeholder="Sprint 14" />
          </label>
          <label>
            <span>Template<RequiredMark /></span>
            <select
              name="templateId"
              onChange={(event) => setTemplateId(event.target.value)}
              required
              value={templateId}
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.teamId ? " · Custom" : ""}
                </option>
              ))}
            </select>
          </label>
          {selectedTemplate && (
            <div className="template-preview">
              <strong>{selectedTemplate.name}</strong>
              <span>{selectedTemplate.description}</span>
              <div>
                {selectedTemplate.sections.map((section) => (
                  <small
                    key={section.id}
                    style={{ borderColor: section.color ?? undefined }}
                  >
                    {section.title}
                  </small>
                ))}
              </div>
            </div>
          )}
          <ErrorBanner>{error}</ErrorBanner>
          <div className="dialog-actions">
            <button className="button secondary" onClick={onClose} type="button">
              Cancel
            </button>
            <button className="button primary" disabled={saving} type="submit">
              {saving ? "Creating…" : "Create retrospective"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ActionRow({
  action,
  canChangeStatus,
  members,
  onDelete,
  onOpenTask,
  onPushToBacklog,
  onSelect,
  onStatus,
  selectDestinations,
  selected,
}: {
  action: ActionItem;
  canChangeStatus: boolean;
  members: Member[];
  onDelete?: (action: ActionItem) => Promise<void>;
  onOpenTask?: (taskId: string) => void;
  onPushToBacklog?: (action: ActionItem, sprintId: string | null) => Promise<void>;
  onSelect?: (id: string, checked: boolean) => void;
  onStatus: (id: string, status: ActionItem["status"]) => Promise<void>;
  selectDestinations?: Array<{ id: string; name: string }>;
  selected?: boolean;
}) {
  const memberNames = members.map((member) => member.displayName);
  // Already a real backlog task (created directly, or via the AI-suggestion
  // accept flow) rather than a pending item_type='action' row -- the
  // status-toggle/push/delete controls below all point at endpoints that no
  // longer act on non-action rows (see the matching comments on updateAction
  // /deleteAction/pushActionToBacklog in portal-api.ts), so show a simpler,
  // read-only pointer into the backlog instead.
  if (action.itemType && action.itemType !== "action") {
    return (
      <article className="action-item action-item-task">
        <div className="action-check"><span aria-hidden="true" className="task-origin-icon">→</span></div>
        <div>
          <strong>{action.taskKey ? `${action.taskKey} ` : ""}{action.title}</strong>
          <p>
            {action.retrospectiveTitle && `${action.retrospectiveTitle} · `}
            Already in the backlog
          </p>
        </div>
        <span className={`status status-${action.status}`}>{action.status.replace("_", " ")}</span>
        <div className="action-owner">
          <Avatar allNames={memberNames} user={{ avatarUrl: action.ownerAvatarUrl, displayName: action.ownerName }} />
          <span>{action.ownerName}</span>
        </div>
        {onOpenTask && (
          <button className="text-button" onClick={() => onOpenTask(action.id)} type="button">
            View in backlog
          </button>
        )}
      </article>
    );
  }
  return (
    <article className={`action-item${onSelect ? " selectable" : ""}`}>
      {onSelect && (
        <input
          aria-label={`Select ${action.title}`}
          checked={Boolean(selected)}
          onChange={(event) => onSelect(action.id, event.target.checked)}
          type="checkbox"
        />
      )}
      <div className="action-check">
        <button
          aria-label={canChangeStatus ? `Mark ${action.title} done` : `Only ${action.ownerName}, the facilitator, or an admin can change this action's status`}
          className={action.status === "done" ? "is-done" : ""}
          disabled={!canChangeStatus}
          onClick={() => onStatus(action.id, action.status === "done" ? "open" : "done")}
          title={canChangeStatus ? undefined : `Only ${action.ownerName}, the facilitator, or an admin can change this action's status`}
          type="button"
        >
          {action.status === "done" ? "✓" : ""}
        </button>
      </div>
      <div>
        <strong>{action.title}</strong>
        <p>
          {action.retrospectiveTitle && `${action.retrospectiveTitle} · `}
          {action.dueOn ? `Due ${formatDate(`${action.dueOn}T12:00:00`)}` : "No due date"}
        </p>
        <div className="action-badges">
          {Boolean(action.isOverdue) && <span className="overdue-badge">Overdue</span>}
          {Boolean(action.isCarriedOver) && <span className="carryover-badge">Carried over</span>}
        </div>
      </div>
      <span className={`status status-${action.status}`}>
        {action.status.replace("_", " ")}
      </span>
      <div className="action-owner">
        <Avatar
          allNames={memberNames}
          user={{
            avatarUrl: action.ownerAvatarUrl,
            displayName: action.ownerName,
          }}
        />
        <span>{action.ownerName}</span>
      </div>
      {onPushToBacklog && selectDestinations && (
        <select
          aria-label={`Move ${action.title} to a sprint or backlog`}
          className="action-move-select"
          defaultValue=""
          onChange={(event) => {
            const value = event.target.value;
            if (!value) return;
            void onPushToBacklog(action, value === "backlog" ? null : value);
            event.currentTarget.value = "";
          }}
          title="Move to backlog or a sprint"
        >
          <option disabled value="">Move to…</option>
          <option value="backlog">Backlog</option>
          {selectDestinations.map((sprint) => (
            <option key={sprint.id} value={sprint.id}>{sprint.name}</option>
          ))}
        </select>
      )}
      {onPushToBacklog && !selectDestinations && (
        <button
          aria-label={`Push ${action.title} to backlog`}
          className="text-button"
          onClick={() => void onPushToBacklog(action, null)}
          title="Push to backlog"
          type="button"
        >
          + Backlog
        </button>
      )}
      {onDelete && (
        <button
          aria-label={`Delete ${action.title}`}
          className="icon-danger"
          onClick={() => void onDelete(action)}
          title="Delete action"
          type="button"
        >
          ×
        </button>
      )}
    </article>
  );
}

function RetroBoard({
  canAdmin,
  currentUserId,
  detail,
  onDeleted,
  onBack,
  onChanged,
  onOpenTask,
}: {
  canAdmin: boolean;
  currentUserId: string;
  detail: RetroDetail;
  onDeleted: () => Promise<void>;
  onBack: () => void;
  onChanged: () => Promise<void>;
  onOpenTask: (taskId: string) => void;
}) {
  const confirm = useConfirm();
  const [cardDrafts, setCardDrafts] = useState<Record<string, TiptapDoc | null>>({});
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editingCardBody, setEditingCardBody] = useState<TiptapDoc | null>(null);
  const [live, setLive] = useState(false);
  const [selectedActionIds, setSelectedActionIds] = useState<Set<string>>(new Set());
  const collecting = detail.retrospective.stage === "collecting";

  useEffect(() => {
    let reconnect: number | undefined;
    let closed = false;
    const connect = () => {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(
        `${protocol}//${window.location.host}/api/realtime/${detail.retrospective.id}`,
      );
      socket.onopen = () => setLive(true);
      socket.onmessage = (event) => {
        if (event.data === "pong") return;
        void onChanged();
      };
      socket.onclose = () => {
        setLive(false);
        if (!closed) reconnect = window.setTimeout(connect, 1500);
      };
    };
    connect();
    return () => {
      closed = true;
      if (reconnect) window.clearTimeout(reconnect);
    };
  }, [detail.retrospective.id, onChanged]);

  const cardsBySection = useMemo(() => {
    const result = new Map<string, RetroCard[]>();
    for (const card of detail.cards) {
      result.set(card.sectionId, [...(result.get(card.sectionId) ?? []), card]);
    }
    return result;
  }, [detail.cards]);
  const cardsById = useMemo(
    () => new Map(detail.cards.map((card) => [card.id, card])),
    [detail.cards],
  );
  const sectionTitlesById = useMemo(
    () => new Map(detail.sections.map((section) => [section.id, section.title])),
    [detail.sections],
  );

  const addCard = async (sectionId: string) => {
    const body = cardDrafts[sectionId];
    if (!body || !docToPlainText(body).trim()) return;
    setBusy(sectionId);
    setError("");
    try {
      await api(`/api/retrospectives/${detail.retrospective.id}/cards`, {
        method: "POST",
        body: JSON.stringify({ body, sectionId }),
      });
      setCardDrafts((current) => ({ ...current, [sectionId]: null }));
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not add note.");
    } finally {
      setBusy("");
    }
  };

  // Creates a real backlog task directly -- no more separate "push to
  // backlog" step -- so estimate/type/sprint are required upfront here,
  // unlike a plain backlog task which can be created unestimated.
  const addAction = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy("action");
    setError("");
    try {
      await api(`/api/retrospectives/${detail.retrospective.id}/tasks`, {
        method: "POST",
        body: JSON.stringify({
          dueOn: form.get("dueOn"),
          estimatePoints: Number(form.get("estimatePoints")),
          ownerId: form.get("ownerId"),
          sprintId: form.get("sprintId"),
          taskType: form.get("taskType"),
          title: form.get("title"),
        }),
      });
      setActionOpen(false);
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create the task.");
    } finally {
      setBusy("");
    }
  };

  const updateStatus = async (id: string, status: ActionItem["status"]) => {
    setBusy(id);
    await api(`/api/work-items/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    await onChanged();
    setBusy("");
  };

  const updateStage = async (stage: string) => {
    setBusy("stage");
    setError("");
    try {
      await api(`/api/retrospectives/${detail.retrospective.id}`, {
        method: "PATCH",
        body: JSON.stringify({ stage }),
      });
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not change stage.");
    } finally {
      setBusy("");
    }
  };

  const updateFacilitator = async (facilitatorId: string) => {
    setBusy("facilitator");
    setError("");
    try {
      await api(`/api/retrospectives/${detail.retrospective.id}`, {
        method: "PATCH",
        body: JSON.stringify({ facilitatorId }),
      });
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not change facilitator.");
    } finally {
      setBusy("");
    }
  };

  const saveCard = async (cardId: string) => {
    if (!editingCardBody || !docToPlainText(editingCardBody).trim()) return;
    setBusy(cardId);
    setError("");
    try {
      await api(
        `/api/retrospectives/${detail.retrospective.id}/cards/${cardId}`,
        { method: "PATCH", body: JSON.stringify({ body: editingCardBody }) },
      );
      setEditingCardId(null);
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update feedback.");
    } finally {
      setBusy("");
    }
  };

  const removeCard = async (card: RetroCard) => {
    const ok = await confirm({ title: "Delete this feedback card?", tone: "danger" });
    if (!ok) return;
    setBusy(card.id);
    try {
      await api(
        `/api/retrospectives/${detail.retrospective.id}/cards/${card.id}`,
        { method: "DELETE" },
      );
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not delete feedback.");
    } finally {
      setBusy("");
    }
  };

  const deleteCurrentRetro = async () => {
    const ok = await confirm({
      title: `Permanently delete "${detail.retrospective.title}", its notes, and its action items?`,
      tone: "danger",
    });
    if (!ok) return;
    setBusy("delete-retro");
    setError("");
    try {
      await api(`/api/retrospectives/${detail.retrospective.id}`, {
        method: "DELETE",
      });
      await onDeleted();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not delete retrospective.");
      setBusy("");
    }
  };

  const deleteActionItem = async (action: ActionItem) => {
    const ok = await confirm({ title: `Permanently delete the action "${action.title}"?`, tone: "danger" });
    if (!ok) return;
    setBusy(action.id);
    setError("");
    try {
      await api(`/api/work-items/${action.id}`, { method: "DELETE" });
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not delete action.");
    } finally {
      setBusy("");
    }
  };

  const pushActionToBacklog = async (action: ActionItem, sprintId: string | null) => {
    setBusy(action.id);
    setError("");
    try {
      await api(`/api/work-items/${action.id}/push-to-backlog`, {
        body: JSON.stringify({ sprintId }),
        method: "POST",
      });
      setSelectedActionIds((value) => { const next = new Set(value); next.delete(action.id); return next; });
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not move this action item.");
    } finally {
      setBusy("");
    }
  };

  const bulkMoveActions = async (sprintId: string | null) => {
    if (!selectedActionIds.size) return;
    setBusy("bulk-move");
    setError("");
    try {
      await api("/api/work-items/bulk-move", {
        body: JSON.stringify({ actionIds: [...selectedActionIds], sprintId }),
        method: "POST",
      });
      setSelectedActionIds(new Set());
      await onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not move the selected action items.");
    } finally {
      setBusy("");
    }
  };

  const generateSuggestions = async () => {
    setBusy("generate-suggestions");
    setError("");
    try {
      await api(
        `/api/retrospectives/${detail.retrospective.id}/action-suggestions`,
        { method: "POST", body: JSON.stringify({}) },
      );
      await onChanged();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not generate action suggestions.",
      );
    } finally {
      setBusy("");
    }
  };

  const reviewSuggestion = async (
    suggestionId: string,
    decision: "accepted" | "rejected",
    edits?: { description: string; title: string },
  ) => {
    setBusy(suggestionId);
    setError("");
    try {
      await api(`/api/action-suggestions/${suggestionId}`, {
        method: "PATCH",
        body: JSON.stringify({ decision, ...edits }),
      });
      await onChanged();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not review suggestion.",
      );
    } finally {
      setBusy("");
    }
  };

  const pendingSuggestions = detail.suggestions.filter(
    (suggestion) => suggestion.status === "pending",
  );
  const suggestionGenerationBusy =
    busy === "generate-suggestions" || detail.suggestionGenerationInProgress;
  const suggestionDisabledReason = !detail.canGenerateActionSuggestions
    ? "Only the facilitator or a team administrator can generate action suggestions."
    : suggestionGenerationBusy
      ? "Action suggestions are already being generated. Everyone must wait for the response."
      : !detail.hasFeedback
        ? "Add at least one retrospective note before generating suggestions."
        : pendingSuggestions.length > 0
          ? "Accept or reject the current suggestions before generating another set."
          : null;

  return (
    <div className="board-view">
      <header className="board-header">
        <div>
          <button className="back-button" onClick={onBack} type="button">
            ← All retrospectives
          </button>
          <p className="mini-label">
            {detail.retrospective.sprintLabel || "Sprint retrospective"}
            <span className={`live-indicator ${live ? "is-live" : ""}`}>
              {live ? "Live" : "Connecting"}
            </span>
          </p>
          <h1>{detail.retrospective.title}</h1>
          <div className="board-summary">
            <span>{detail.summary.cardCount} feedback cards</span>
            <span>{detail.summary.completedActionCount}/{detail.summary.actionCount} actions complete</span>
            <span>{detail.summary.overdueActionCount} overdue</span>
          </div>
        </div>
        <div className="board-controls">
          {detail.canControlStage ? (
            <>
              <label className="stage-control">
                Facilitator
                <select
                  disabled={busy === "facilitator"}
                  onChange={(event) => void updateFacilitator(event.target.value)}
                  value={detail.retrospective.facilitatorId}
                >
                  {detail.members.map((member) => (
                    <option key={member.id} value={member.id}>{member.displayName}</option>
                  ))}
                </select>
              </label>
              <label className="stage-control">
                Stage
                <select
                  aria-label="Facilitator stage"
                  disabled={busy === "stage"}
                  onChange={(event) => void updateStage(event.target.value)}
                  value={detail.retrospective.stage}
                >
                  <option value="draft">Draft</option>
                  <option value="collecting">Collecting</option>
                  <option value="discussing">Discussing</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
            </>
          ) : (
            <span className="stage-pill">{detail.retrospective.stage}</span>
          )}
          {canAdmin && (
            <button
              className="button danger"
              disabled={busy === "delete-retro"}
              onClick={() => void deleteCurrentRetro()}
              type="button"
            >
              Delete retrospective
            </button>
          )}
        </div>
      </header>
      <ErrorBanner>{error}</ErrorBanner>
      <section className="retro-board">
        {detail.sections.map((section) => (
          <article className="retro-column" key={section.id}>
            <header style={{ borderColor: section.color ?? "#d9d4c8" }}>
              <div>
                <h2>{section.title}</h2>
                <p>{section.prompt}</p>
              </div>
              <span>{cardsBySection.get(section.id)?.length ?? 0}</span>
            </header>
            <div className="cards">
              {(cardsBySection.get(section.id) ?? []).map((card) => (
                <article className="retro-note" key={card.id}>
                  {editingCardId === card.id ? (
                    <div className="note-editor">
                      <RichTextEditor
                        ariaLabel="Edit feedback card"
                        autofocus
                        characterLimit={1000}
                        disabled={busy === card.id}
                        members={detail.members}
                        onChange={setEditingCardBody}
                        placeholder="Type @ to mention someone"
                        value={editingCardBody}
                      />
                    </div>
                  ) : (
                    <RichTextView doc={card.body} />
                  )}
                  <footer>
                    <Avatar
                      allNames={detail.members.map((member) => member.displayName)}
                      user={{
                        avatarUrl: card.authorAvatarUrl,
                        displayName: card.authorName,
                      }}
                    />
                    <span>{card.authorName}</span>
                    {(detail.canManageCards ||
                      (card.authorId === currentUserId && collecting)) && (
                      <span className="card-actions">
                        {editingCardId === card.id ? (
                          <>
                            <button onClick={() => void saveCard(card.id)} type="button">Save</button>
                            <button onClick={() => setEditingCardId(null)} type="button">Cancel</button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingCardId(card.id);
                                setEditingCardBody(card.body);
                              }}
                              type="button"
                            >Edit</button>
                            <button onClick={() => void removeCard(card)} type="button">Delete</button>
                          </>
                        )}
                      </span>
                    )}
                  </footer>
                </article>
              ))}
            </div>
            <NoteComposer
              ariaLabel={`Add a note to ${section.title}`}
              busy={busy === section.id}
              disabled={!collecting}
              members={detail.members}
              onChange={(value) =>
                setCardDrafts((current) => ({
                  ...current,
                  [section.id]: value,
                }))
              }
              onSubmit={() => void addCard(section.id)}
              value={cardDrafts[section.id] ?? null}
            />
          </article>
        ))}
      </section>
      {!collecting && (
        <p className="collection-note">
          Note collection is paused while this retrospective is {detail.retrospective.stage}.
          {detail.canControlStage && " Change the facilitator stage to Collecting to reopen it."}
        </p>
      )}
      {detail.retrospective.stage === "discussing" && (
        <section className="suggestion-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">AI-assisted follow-through</p>
              <h2>Potential action items</h2>
              <p className="section-description">
                Taskei analyzes each author’s notes across every retrospective section and proposes
                a measurable experiment. Nothing becomes an action until it is accepted.
              </p>
            </div>
            <div className="suggestion-generate-control">
              <button
                className="button primary"
                disabled={Boolean(suggestionDisabledReason)}
                onClick={() => void generateSuggestions()}
                type="button"
              >
                {suggestionGenerationBusy
                  ? "Thinking through feedback…"
                  : "Generate AI action recommendations"}
              </button>
              {suggestionDisabledReason && (
                <p className="suggestion-access-note">{suggestionDisabledReason}</p>
              )}
            </div>
          </div>
          {!detail.hasFeedback ? (
            <p className="empty-inline">
              Add at least one retrospective note to generate suggestions.
            </p>
          ) : pendingSuggestions.length ? (
            <div className="suggestion-list">
              {pendingSuggestions.map((suggestion) => (
                <article className="suggestion-card" key={suggestion.id}>
                  <form onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); void reviewSuggestion(suggestion.id, "accepted", { description: String(form.get("description") ?? ""), title: String(form.get("title") ?? "") }); }}>
                  <header>
                    <div>
                      <span className="suggestion-owner">
                        Suggested for {suggestion.authorName}
                      </span>
                      <h3>{suggestion.title}</h3><label><span>Edit task title<RequiredMark /></span><input defaultValue={suggestion.title} name="title" required /></label>
                    </div>
                    <span className="stage-pill">Proposed</span>
                  </header>
                  <label><span>Task description<RequiredMark /></span><textarea defaultValue={suggestion.description} name="description" required rows={3} /></label>
                  <dl>
                    <div>
                      <dt>Success metric</dt>
                      <dd>{suggestion.successMetric}</dd>
                    </div>
                    <div>
                      <dt>Target</dt>
                      <dd>{suggestion.targetValue}</dd>
                    </div>
                    <div>
                      <dt>Suggested due date</dt>
                      <dd>{formatDate(suggestion.suggestedDueOn)}</dd>
                    </div>
                    <div>
                      <dt>Why this action</dt>
                      <dd>{suggestion.rationale}</dd>
                    </div>
                    <div className="suggestion-evidence">
                      <dt>Grounded in notes</dt>
                      <dd>
                        <ul>
                          {suggestion.sourceCardIds.map((cardId) => {
                            const card = cardsById.get(cardId);
                            if (!card) return null;
                            return (
                              <li key={cardId}>
                                <strong>
                                  {sectionTitlesById.get(card.sectionId) ?? "Feedback"}:
                                </strong>{" "}
                                {docToPlainText(card.body)}
                              </li>
                            );
                          })}
                        </ul>
                      </dd>
                    </div>
                  </dl>
                  {detail.canGenerateActionSuggestions && (
                    <footer>
                      <button
                        className="button primary"
                        disabled={busy === suggestion.id}
                        type="submit"
                      >
                        Accept and create action
                      </button>
                      <button
                        className="button secondary"
                        disabled={busy === suggestion.id}
                        onClick={() =>
                          void reviewSuggestion(suggestion.id, "rejected")
                        }
                        type="button"
                      >
                        Reject
                      </button>
                    </footer>
                  )}
                  </form>
                </article>
              ))}
            </div>
          ) : (
            <p className="empty-inline">
              No suggestions are awaiting review. Generate a set when the team is ready.
            </p>
          )}
        </section>
      )}
      <section className="retro-actions">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Follow-through</p>
            <h2>Action items</h2>
          </div>
          {!detail.retrospective.isReadOnly && <button className="button primary" onClick={() => setActionOpen(true)} type="button">
            + Add action
          </button>}
        </div>
        {actionOpen && !detail.sprints.length && (
          <p className="empty-inline">This team has no planned or active sprint yet -- create one from the Backlog or Sprints tab before adding an action item here.</p>
        )}
        {actionOpen && (
          <form className="action-form" onSubmit={addAction}>
            <label>
              <span>Action<RequiredMark /></span>
              <input autoFocus name="title" placeholder="What needs to happen next?" required />
            </label>
            <label>
              <span>Owner<RequiredMark /></span>
              <select defaultValue={detail.members[0]?.id} name="ownerId" required>
                {detail.members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Due date
              <input name="dueOn" type="date" />
            </label>
            <label>
              <span>Task type<RequiredMark /></span>
              <select defaultValue="task" name="taskType" required>
                {ISSUE_TYPES.map((type) => (
                  <option key={type} value={type}>{issueTypeMeta(type).label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Story points<RequiredMark /></span>
              <input min="0" name="estimatePoints" placeholder="0" required step="0.25" type="number" />
            </label>
            <label>
              <span>Sprint<RequiredMark /></span>
              <select defaultValue={detail.sprints[0]?.id ?? ""} name="sprintId" required>
                <option disabled value="">Choose a sprint</option>
                {detail.sprints.map((sprint) => (
                  <option key={sprint.id} value={sprint.id}>{sprint.name}</option>
                ))}
              </select>
            </label>
            <button className="button primary" disabled={busy === "action" || !detail.sprints.length} title={detail.sprints.length ? undefined : "This team has no planned or active sprint to add the task to yet."} type="submit">
              {busy === "action" ? "Creating…" : "Create task"}
            </button>
            <button className="button secondary" onClick={() => setActionOpen(false)} type="button">
              Cancel
            </button>
          </form>
        )}
        {detail.actions.length > 0 && (
          <div className="action-bulk-bar">
            <label className="action-bulk-select-all">
              <input
                aria-label="Select all action items"
                checked={selectedActionIds.size > 0 && selectedActionIds.size === detail.actions.length}
                onChange={(event) => setSelectedActionIds(
                  event.target.checked ? new Set(detail.actions.map((action) => action.id)) : new Set(),
                )}
                type="checkbox"
              />
              {selectedActionIds.size ? `${selectedActionIds.size} selected` : "Select all"}
            </label>
            {selectedActionIds.size > 0 && (
              <select
                aria-label="Move selected action items to a sprint or backlog"
                defaultValue=""
                disabled={busy === "bulk-move"}
                onChange={(event) => {
                  const value = event.target.value;
                  if (!value) return;
                  void bulkMoveActions(value === "backlog" ? null : value);
                  event.currentTarget.value = "";
                }}
              >
                <option disabled value="">Move selected to…</option>
                <option value="backlog">Backlog</option>
                {detail.sprints.map((sprint) => (
                  <option key={sprint.id} value={sprint.id}>{sprint.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="action-list">
          {detail.actions.length ? (
            detail.actions.map((action) => (
              <ActionRow
                action={action}
                canChangeStatus={canAdmin || action.ownerId === currentUserId || action.facilitatorId === currentUserId}
                key={action.id}
                members={detail.members}
                onDelete={canAdmin ? deleteActionItem : undefined}
                onOpenTask={onOpenTask}
                onPushToBacklog={pushActionToBacklog}
                onSelect={(id, checked) => setSelectedActionIds((value) => {
                  const next = new Set(value);
                  if (checked) next.add(id); else next.delete(id);
                  return next;
                })}
                onStatus={updateStatus}
                selectDestinations={detail.sprints}
                selected={selectedActionIds.has(action.id)}
              />
            ))
          ) : (
            <div className="empty-inline">No actions yet. Capture the next concrete step.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function ImportsWorkspace({ members, onImported, teamId, templates }: { members: User[]; onImported: () => Promise<void>; teamId: string; templates: RetroTemplate[] }) {
  const [source, setSource] = useState<"jira" | "confluence">("jira");
  return (
    <section className="imports-workspace">
      <div className="section-heading"><div><p className="eyebrow">External work</p><h2>Imports</h2><p className="section-description">Bring delivery work and historical retrospectives into this team.</p></div></div>
      <div aria-label="Import sources" className="source-tabs" role="tablist">
        <button aria-selected={source === "jira"} onClick={() => setSource("jira")} role="tab" type="button">Jira</button>
        <button aria-selected={source === "confluence"} onClick={() => setSource("confluence")} role="tab" type="button">Confluence</button>
      </div>
      {source === "jira" ? <JiraImportPanel teamId={teamId} /> : <ConfluenceImportPanel members={members} onImported={onImported} teamId={teamId} templates={templates} />}
    </section>
  );
}

function Administration({
  team,
  user,
  onWorkspaceChanged,
  onTeamArchived,
}: {
  team: Team;
  user: User;
  onWorkspaceChanged: () => Promise<void>;
  onTeamArchived: () => Promise<void>;
}) {
  const confirm = useConfirm();
  const showToast = useToast();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [sectionCount, setSectionCount] = useState(3);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiModel, setAiModel] = useState("gpt-5.6-terra");
  const [aiReasoningEffort, setAiReasoningEffort] = useState<
    "low" | "medium" | "high"
  >("medium");
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refineModel, setRefineModel] = useState("gpt-5.6-terra");
  const [refineReasoningEffort, setRefineReasoningEffort] = useState<
    "low" | "medium" | "high"
  >("medium");
  const [openAiBilling, setOpenAiBilling] = useState<OpenAiBilling | null>(null);
  const [openAiBillingError, setOpenAiBillingError] = useState("");
  // Account-wide, not team-scoped (see migrations/0036_platform_settings.sql)
  // and only visible to portal admins -- loaded separately from `overview`
  // rather than folded into AdminOverview, same reason openAiBilling is
  // loaded separately: a 403 here (non-portal-admin) shouldn't block the
  // rest of a team admin's page.
  const [branchCleanup, setBranchCleanup] = useState<BranchCleanupSettings | null>(null);

  const load = useCallback(async () => {
    const [result, taskAccess] = await Promise.all([
      api<AdminOverview>(`/api/admin?teamId=${encodeURIComponent(team.id)}`),
      api<{ members: Array<{ permissions: TaskPermission[]; userId: string }> }>(
        `/api/teams/${encodeURIComponent(team.id)}/task-permissions`,
      ),
    ]);
    const permissionsByUser = new Map(
      taskAccess.members.map((member) => [member.userId, member.permissions]),
    );
    setOverview({
      ...result,
      members: result.members.map((member) => ({
        ...member,
        taskPermissions: permissionsByUser.get(member.id) ?? [],
      })),
    });
    setAiPrompt(result.aiSettings.prompt);
    setAiModel(result.aiSettings.model);
    setAiReasoningEffort(result.aiSettings.reasoningEffort);
    setRefinePrompt(result.aiSettings.refinePrompt);
    setRefineModel(result.aiSettings.refineModel);
    setRefineReasoningEffort(result.aiSettings.refineReasoningEffort);
  }, [team.id]);

  const loadBranchCleanup = useCallback(async () => {
    try {
      setBranchCleanup(await api<BranchCleanupSettings>("/api/admin/branch-cleanup"));
    } catch {
      // Expected for non-portal-admins (403 PORTAL_ADMIN_REQUIRED) -- this
      // section simply stays hidden rather than surfacing an error banner
      // for every team admin who isn't also a portal admin.
      setBranchCleanup(null);
    }
  }, []);

  const setTaskPermission = async (
    member: AdminMember,
    permission: TaskPermission,
    enabled: boolean,
  ) => {
    setBusy(`${member.id}:${permission}`);
    setError("");
    const permissions = enabled
      ? [...new Set([...member.taskPermissions, permission])]
      : member.taskPermissions.filter((candidate) => candidate !== permission);
    try {
      await api(
        `/api/teams/${team.id}/members/${member.id}/task-permissions`,
        { body: JSON.stringify({ permissions }), method: "PUT" },
      );
      setNotice(`Task permissions updated for ${member.displayName}.`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update permissions.");
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    load().catch((reason) =>
      setError(reason instanceof Error ? reason.message : "Could not load administration."),
    );
  }, [load]);

  useEffect(() => {
    void loadBranchCleanup();
  }, [loadBranchCleanup]);

  useEffect(() => {
    setOpenAiBilling(null);
    setOpenAiBillingError("");
    api<OpenAiBilling>(
      `/api/admin/openai-billing?teamId=${encodeURIComponent(team.id)}`,
    )
      .then(setOpenAiBilling)
      .catch((reason) =>
        setOpenAiBillingError(
          reason instanceof Error ? reason.message : "Could not load OpenAI billing data.",
        ),
      );
  }, [team.id]);

  const invite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setBusy("invite");
    setError("");
    setNotice("");
    try {
      const result = await api<{ email: string; status: "added" | "invited" }>(
        "/api/admin/invitations",
        {
          method: "POST",
          body: JSON.stringify({
            email: form.get("email"),
            role: form.get("role"),
            teamId: team.id,
          }),
        },
      );
      setNotice(
        result.status === "added"
          ? `${result.email} was added to the team.`
          : `Invitation sent to ${result.email}.`,
      );
      formElement.reset();
      await Promise.all([load(), onWorkspaceChanged()]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not invite user.");
    } finally {
      setBusy("");
    }
  };

  const setRole = async (memberId: string, role: "admin" | "member") => {
    setBusy(memberId);
    setError("");
    try {
      await api(`/api/admin/members/${memberId}`, {
        method: "PATCH",
        body: JSON.stringify({ role, teamId: team.id }),
      });
      await Promise.all([load(), onWorkspaceChanged()]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update member.");
    } finally {
      setBusy("");
    }
  };

  // Global, not team-scoped -- see the isPortalAdminUser comment in
  // portal-api.ts. Grants Authoring (onboarding plan creation/editing) across
  // every team, so it's kept separate from the team-role select above.
  const setPortalAdmin = async (member: AdminMember, portalAdmin: boolean) => {
    setBusy(member.id);
    setError("");
    try {
      await api(`/api/admin/members/${member.id}/portal-role`, {
        method: "PATCH",
        body: JSON.stringify({ portalAdmin }),
      });
      await load();
      showToast(
        portalAdmin
          ? `${member.displayName} can now author onboarding plans for every team.`
          : `${member.displayName} is no longer a portal admin.`,
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update portal admin access.");
    } finally {
      setBusy("");
    }
  };

  const remove = async (member: AdminMember) => {
    const ok = await confirm({ title: `Remove ${member.displayName} from this Taskei team?`, tone: "danger" });
    if (!ok) return;
    setBusy(member.id);
    setError("");
    try {
      await api(`/api/admin/members/${member.id}`, {
        method: "DELETE",
        body: JSON.stringify({ teamId: team.id }),
      });
      await Promise.all([load(), onWorkspaceChanged()]);
      showToast(`${member.displayName} was removed from the team.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not remove member.");
    } finally {
      setBusy("");
    }
  };

  const revoke = async (invitationId: string) => {
    setBusy(invitationId);
    setError("");
    try {
      await api(`/api/admin/invitations/${invitationId}`, {
        method: "DELETE",
        body: JSON.stringify({ teamId: team.id }),
      });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not revoke invitation.");
    } finally {
      setBusy("");
    }
  };

  const createTemplate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const sections = Array.from({ length: sectionCount }, (_, index) => ({
      prompt: form.get(`sectionPrompt${index}`),
      title: form.get(`sectionTitle${index}`),
    }));
    setBusy("template");
    setError("");
    setNotice("");
    try {
      await api("/api/admin/templates", {
        method: "POST",
        body: JSON.stringify({
          description: form.get("description"),
          name: form.get("name"),
          sections,
          teamId: team.id,
        }),
      });
      setNotice("Custom retrospective template created.");
      formElement.reset();
      setSectionCount(3);
      await Promise.all([load(), onWorkspaceChanged()]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create template.");
    } finally {
      setBusy("");
    }
  };

  const setTemplateStatus = async (
    templateId: string,
    status: "active" | "archived",
  ) => {
    setBusy(templateId);
    setError("");
    try {
      await api(`/api/admin/templates/${templateId}`, {
        method: "PATCH",
        body: JSON.stringify({ status, teamId: team.id }),
      });
      await Promise.all([load(), onWorkspaceChanged()]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update template.");
    } finally {
      setBusy("");
    }
  };

  const updateTeamName = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy("team-name");
    setError("");
    try {
      await api(`/api/teams/${team.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: form.get("name") }),
      });
      setNotice("Team name updated.");
      await onWorkspaceChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update team.");
    } finally {
      setBusy("");
    }
  };

  const archiveTeam = async () => {
    const ok = await confirm({ title: `Archive ${team.name}? Its data will be preserved.`, tone: "danger" });
    if (!ok) return;
    setBusy("archive-team");
    try {
      await api(`/api/teams/${team.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "archived" }),
      });
      await onTeamArchived();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not archive team.");
      setBusy("");
    }
  };

  const purgeAllTasks = async () => {
    const ok = await confirm({
      confirmLabel: "Purge all tasks",
      description: "This permanently deletes every task, story, epic, subtask, and bug for this team. Retrospective action items are not affected.",
      requireText: team.name,
      title: `Purge all tasks for ${team.name}?`,
      tone: "danger",
    });
    if (!ok) return;
    setBusy("purge-tasks");
    setError("");
    try {
      const result = await api<{ purgedTaskCount: number }>(`/api/teams/${team.id}/tasks/purge`, {
        method: "POST",
      });
      setNotice(`Deleted ${result.purgedTaskCount} task${result.purgedTaskCount === 1 ? "" : "s"} from ${team.name}.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not purge tasks.");
    } finally {
      setBusy("");
    }
  };

  const saveAiPrompt = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy("ai-prompt");
    setError("");
    setNotice("");
    try {
      await api("/api/admin/ai-settings", {
        method: "PATCH",
        body: JSON.stringify({
          model: aiModel,
          prompt: aiPrompt,
          reasoningEffort: aiReasoningEffort,
          refineModel,
          refinePrompt,
          refineReasoningEffort,
          teamId: team.id,
        }),
      });
      setNotice("AI action-suggestion settings saved for this team.");
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save AI prompt.");
    } finally {
      setBusy("");
    }
  };

  const saveRefinePrompt = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy("refine-prompt");
    setError("");
    setNotice("");
    try {
      await api("/api/admin/ai-settings", {
        method: "PATCH",
        body: JSON.stringify({
          model: aiModel,
          prompt: aiPrompt,
          reasoningEffort: aiReasoningEffort,
          refineModel,
          refinePrompt,
          refineReasoningEffort,
          teamId: team.id,
        }),
      });
      setNotice("AI task-refinement settings saved for this team.");
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save the refinement prompt.");
    } finally {
      setBusy("");
    }
  };

  const toggleBranchCleanup = async (enabled: boolean) => {
    setBusy("branch-cleanup");
    setError("");
    setNotice("");
    try {
      const result = await api<BranchCleanupSettings>("/api/admin/branch-cleanup", {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      });
      setBranchCleanup(result);
      setNotice(
        enabled
          ? "Auto-delete merged branches is now on for every CodeCommit repo."
          : "Auto-delete merged branches is now off for every CodeCommit repo.",
      );
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not update the branch cleanup rule.",
      );
    } finally {
      setBusy("");
    }
  };

  const testOpenAi = async () => {
    setBusy("openai-check");
    setError("");
    setNotice("");
    try {
      await api("/api/admin/ai-settings/test", {
        method: "POST",
        body: JSON.stringify({ teamId: team.id }),
      });
      setNotice("OpenAI integration check passed.");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not complete the OpenAI integration check.",
      );
    } finally {
      await load().catch(() => undefined);
      setBusy("");
    }
  };

  if (!overview) {
    return error ? <ErrorBanner>{error}</ErrorBanner> : <Skeleton label="Loading administration…" variant="list" />;
  }

  return (
    <section className="admin-page">
      <ErrorBanner>{error}</ErrorBanner>
      {notice && <p className="banner-success">{notice}</p>}

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Team settings</p>
            <h2>Manage {team.name}</h2>
          </div>
        </div>
        <form className="team-settings-form" onSubmit={updateTeamName}>
          <label>
            <span>Team name<RequiredMark /></span>
            <input defaultValue={team.name} name="name" required />
          </label>
          <button className="button secondary" disabled={busy === "team-name"} type="submit">
            Save name
          </button>
          <button
            className="button danger"
            disabled={busy === "archive-team"}
            onClick={() => void archiveTeam()}
            type="button"
          >
            Archive team
          </button>
        </form>
      </section>

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Danger zone</p>
            <h2>Purge all tasks</h2>
            <p className="section-description">
              Permanently delete every task, story, epic, subtask, and bug for {team.name}. Retrospective
              action items are not affected. This cannot be undone.
            </p>
          </div>
        </div>
        <button
          className="button danger"
          disabled={busy === "purge-tasks"}
          onClick={() => void purgeAllTasks()}
          type="button"
        >
          {busy === "purge-tasks" ? "Purging…" : "Purge all tasks"}
        </button>
      </section>

      {branchCleanup && (
        <section className="admin-panel wide">
          <div className="section-heading">
            <div>
              <p className="eyebrow">CodeCommit automation · account-wide</p>
              <h2>Auto-delete merged branches</h2>
              <p className="section-description">
                When on, Taskei's AWS automation (EventBridge rule {branchCleanup.ruleName}) deletes a
                pull request's source branch a few seconds after it merges, for every CodeCommit
                repository in the account. Branches named main, master, develop, dev, prod, or alpha
                are never deleted. This applies to all teams, not just {team.name}.
              </p>
            </div>
          </div>
          <div
            className={`integration-status integration-status-${
              branchCleanup.enabled ? "operational" : "failed"
            }`}
            role="status"
          >
            <span className="integration-status-dot" aria-hidden="true" />
            <div>
              <strong>{branchCleanup.enabled ? "Auto-delete is on" : "Auto-delete is off"}</strong>
              <p>
                {branchCleanup.liveState === "UNKNOWN"
                  ? "Taskei could not confirm the live AWS rule state; showing the last known setting."
                  : "Reflects the live AWS EventBridge rule state, not just what Taskei last requested."}
              </p>
              {branchCleanup.updatedAt && (
                <small>
                  Last changed {formatDateTime(branchCleanup.updatedAt)}
                  {branchCleanup.updatedBy ? ` by ${branchCleanup.updatedBy}` : ""}
                </small>
              )}
            </div>
            <button
              className={`button ${branchCleanup.enabled ? "danger" : "secondary"}`}
              disabled={busy === "branch-cleanup"}
              onClick={() => void toggleBranchCleanup(!branchCleanup.enabled)}
              type="button"
            >
              {busy === "branch-cleanup"
                ? "Updating…"
                : branchCleanup.enabled
                  ? "Turn off"
                  : "Turn on"}
            </button>
          </div>
        </section>
      )}

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Action suggestion guidance</p>
            <h2>Configure AI action suggestions</h2>
            <p className="section-description">
              This guidance is applied when a facilitator generates potential actions
              from each author’s notes across every retrospective section. The app keeps
              authors separate, validates source-note references, and enforces the
              measurable output format.
            </p>
          </div>
          <span className="model-label">
            {overview.aiSettings.model} · {overview.aiSettings.reasoningEffort}
          </span>
        </div>
        <div
          className={`integration-status integration-status-${overview.aiSettings.connectionStatus}`}
          role="status"
        >
          <span className="integration-status-dot" aria-hidden="true" />
          <div>
            <strong>
              {overview.aiSettings.connectionStatus === "operational"
                ? "OpenAI integration operational"
                : overview.aiSettings.connectionStatus === "failed"
                  ? "OpenAI integration needs attention"
                  : "OpenAI integration not tested"}
            </strong>
            <p>
              {overview.aiSettings.connectionMessage ||
                "Run a live check to verify the Worker secret, API credit, model access, and structured responses."}
            </p>
            {overview.aiSettings.connectionCheckedAt && (
              <small>
                Last checked {formatDateTime(overview.aiSettings.connectionCheckedAt)}
                {overview.aiSettings.connectionLatencyMs
                  ? ` · ${overview.aiSettings.connectionLatencyMs.toLocaleString()} ms`
                  : ""}
                {overview.aiSettings.connectionErrorCode
                  ? ` · ${overview.aiSettings.connectionErrorCode}`
                  : ""}
              </small>
            )}
          </div>
          <button
            className="button secondary"
            disabled={busy === "openai-check"}
            onClick={() => void testOpenAi()}
            type="button"
          >
            {busy === "openai-check" ? "Testing…" : "Test OpenAI connection"}
          </button>
        </div>
        <div className="openai-billing-card">
          <div>
            <strong>OpenAI API billing</strong>
            {openAiBilling?.monthToDateCost != null ? (
              <p>
                Month-to-date spend: <strong>${openAiBilling.monthToDateCost.toFixed(2)} USD</strong>
              </p>
            ) : (
              <p>
                {openAiBillingError ||
                  (openAiBilling?.adminKeyConfigured === false
                    ? "Add the OPENAI_ADMIN_API_KEY Worker secret to show live organization spend."
                    : "Loading billing data…")}
              </p>
            )}
            <small>
              OpenAI does not expose the remaining prepaid-credit balance through its
              supported API. Open Billing to see the authoritative balance and add credits.
            </small>
          </div>
          <a
            className="button secondary"
            href={openAiBilling?.billingUrl ?? "https://platform.openai.com/settings/organization/billing/overview"}
            rel="noreferrer"
            target="_blank"
          >
            Open billing
          </a>
        </div>
        <form className="ai-prompt-form" onSubmit={saveAiPrompt}>
          <div className="ai-configuration-grid">
            <label>
              Model
              <select
                onChange={(event) => setAiModel(event.target.value)}
                value={aiModel}
              >
                <option value="gpt-5.6-terra">GPT-5.6 Terra · Recommended balance</option>
                <option value="gpt-5.6-luna">GPT-5.6 Luna · Lowest cost</option>
                <option value="gpt-5.6-sol">GPT-5.6 Sol · Highest capability and cost</option>
              </select>
            </label>
            <label>
              Reasoning complexity
              <select
                onChange={(event) =>
                  setAiReasoningEffort(
                    event.target.value as "low" | "medium" | "high",
                  )
                }
                value={aiReasoningEffort}
              >
                <option value="low">Low · Faster and cheaper</option>
                <option value="medium">Medium · Recommended</option>
                <option value="high">High · More reasoning and cost</option>
              </select>
            </label>
          </div>
          <p className="admin-help">
            Changing the model or complexity requires running the integration check again.
          </p>
          <label htmlFor="action-suggestion-prompt"><span>Team prompt<RequiredMark /></span></label>
          <textarea
            id="action-suggestion-prompt"
            minLength={200}
            onChange={(event) => setAiPrompt(event.target.value)}
            required
            rows={12}
            value={aiPrompt}
          />
          <div className="form-actions">
            <button
              className="button primary"
              disabled={busy === "ai-prompt"}
              type="submit"
            >
              {busy === "ai-prompt" ? "Saving…" : "Save AI settings"}
            </button>
            <small>{aiPrompt.length.toLocaleString()} / 8,000 characters</small>
          </div>
        </form>
      </section>

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Task refinement guidance</p>
            <h2>Configure AI task refinement</h2>
            <p className="section-description">
              This is the model and prompt used by the “Refine with AI” icon on a task's
              title and description. It always shows a preview first — nothing is saved to a
              task until a team member explicitly accepts the suggestion.
            </p>
          </div>
          <span className="model-label">
            {overview.aiSettings.refineModel} · {overview.aiSettings.refineReasoningEffort}
          </span>
        </div>
        <form className="ai-prompt-form" onSubmit={saveRefinePrompt}>
          <div className="ai-configuration-grid">
            <label>
              Model
              <select
                onChange={(event) => setRefineModel(event.target.value)}
                value={refineModel}
              >
                <option value="gpt-5.6-terra">GPT-5.6 Terra · Recommended balance</option>
                <option value="gpt-5.6-luna">GPT-5.6 Luna · Lowest cost</option>
                <option value="gpt-5.6-sol">GPT-5.6 Sol · Highest capability and cost</option>
              </select>
            </label>
            <label>
              Reasoning complexity
              <select
                onChange={(event) =>
                  setRefineReasoningEffort(
                    event.target.value as "low" | "medium" | "high",
                  )
                }
                value={refineReasoningEffort}
              >
                <option value="low">Low · Faster and cheaper</option>
                <option value="medium">Medium · Recommended</option>
                <option value="high">High · More reasoning and cost</option>
              </select>
            </label>
          </div>
          <label htmlFor="task-refinement-prompt"><span>Refinement prompt<RequiredMark /></span></label>
          <textarea
            id="task-refinement-prompt"
            minLength={50}
            onChange={(event) => setRefinePrompt(event.target.value)}
            required
            rows={10}
            value={refinePrompt}
          />
          <div className="form-actions">
            <button
              className="button primary"
              disabled={busy === "refine-prompt"}
              type="submit"
            >
              {busy === "refine-prompt" ? "Saving…" : "Save refinement settings"}
            </button>
            <small>{refinePrompt.length.toLocaleString()} / 8,000 characters</small>
          </div>
        </form>
      </section>

      <div className="admin-grid">
        <section className="admin-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Workspace access</p>
              <h2>Invite a team member</h2>
            </div>
          </div>
          <form className="invite-form" onSubmit={invite}>
            <label>
              <span>Novixpert email<RequiredMark /></span>
              <input
                name="email"
                pattern=".+@novixpert\.com"
                placeholder="name@novixpert.com"
                required
                type="email"
              />
            </label>
            <label>
              Role
              <select defaultValue="member" name="role">
                <option value="member">Member</option>
                <option value="admin">Administrator</option>
              </select>
            </label>
            <button className="button primary" disabled={busy === "invite"} type="submit">
              {busy === "invite" ? "Inviting…" : "Send invitation"}
            </button>
          </form>
          <p className="admin-help">
            Invitations only work for Google Workspace accounts ending in @novixpert.com.
          </p>
        </section>

        <section className="admin-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Pending access</p>
              <h2>Invitations</h2>
            </div>
          </div>
          {overview.invitations.length ? (
            <div className="simple-list">
              {overview.invitations.map((invitation) => (
                <article key={invitation.id}>
                  <div>
                    <strong>{invitation.email}</strong>
                    <small>
                      {invitation.role} · Sent {formatDate(invitation.createdAt)}
                    </small>
                  </div>
                  <button
                    className="text-button danger"
                    disabled={busy === invitation.id}
                    onClick={() => void revoke(invitation.id)}
                    type="button"
                  >
                    Revoke
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <p className="admin-help">No pending invitations.</p>
          )}
        </section>
      </div>

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">People and permissions</p>
            <h2>Team members</h2>
          </div>
          <span className="count-pill">{overview.members.length}</span>
        </div>
        <div className={overview.viewerIsPortalAdmin ? "member-table with-portal-column" : "member-table"}>
          {overview.members.map((member) => (
            <article key={member.id}>
              <Avatar allNames={overview.members.map((entry) => entry.displayName)} user={member} />
              <div>
                <strong>{member.displayName}</strong>
                <small>{member.email}</small>
              </div>
              <select
                aria-label={`Role for ${member.displayName}`}
                disabled={busy === member.id}
                onChange={(event) =>
                  void setRole(
                    member.id,
                    event.target.value as "admin" | "member",
                  )
                }
                value={member.role}
              >
                <option value="member">Member</option>
                <option value="admin">Administrator</option>
              </select>
              {overview.viewerIsPortalAdmin ? (
                <label
                  className="portal-admin-toggle"
                  title="Portal admin can author onboarding plans for every team, not just this one."
                >
                  <input
                    aria-label={`Portal admin for ${member.displayName}`}
                    checked={Boolean(member.isPortalAdmin)}
                    disabled={busy === member.id}
                    onChange={(event) => void setPortalAdmin(member, event.target.checked)}
                    type="checkbox"
                  />
                  Portal admin
                </label>
              ) : null}
              <button
                className="text-button danger"
                disabled={member.id === user.id || busy === member.id}
                onClick={() => void remove(member)}
                type="button"
              >
                Remove
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Delegated access</p>
            <h2>Task-management permissions</h2>
            <p className="section-description">
              Grant focused capabilities without making someone a team administrator.
              Administrators always have every capability.
            </p>
          </div>
        </div>
        <div className="permission-matrix">
          <header>
            <span>Member</span>{(["manage_tasks", "manage_board", "manage_sprints", "manage_integrations", "view_task_reports"] as TaskPermission[]).map((permission) => <span key={permission} title={TASK_PERMISSION_DETAILS[permission].description}>{TASK_PERMISSION_DETAILS[permission].label}<small>{TASK_PERMISSION_DETAILS[permission].description}</small></span>)}
          </header>
          {overview.members.map((member) => (
            <article key={member.id}>
              <div><strong>{member.displayName}</strong><small>{member.role}</small></div>
              {([
                "manage_tasks",
                "manage_board",
                "manage_sprints",
                "manage_integrations",
                "view_task_reports",
              ] as TaskPermission[]).map((permission) => (
                <label key={permission}>
                  <input
                    aria-label={`${TASK_PERMISSION_DETAILS[permission].label} for ${member.displayName}`}
                    checked={member.role === "admin" || member.taskPermissions.includes(permission)}
                    disabled={member.role === "admin" || busy === `${member.id}:${permission}`}
                    onChange={(event) => void setTaskPermission(member, permission, event.target.checked)}
                    type="checkbox"
                  />
                </label>
              ))}
            </article>
          ))}
        </div>
      </section>

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Reusable formats</p>
            <h2>Retrospective templates</h2>
          </div>
        </div>
        <div className="template-admin-grid">
          {overview.templates.map((template) => (
            <article
              className={template.status === "archived" ? "is-archived" : ""}
              key={template.id}
            >
              <div>
                <span>{template.teamId ? "Custom" : "Built-in"}</span>
                <h3>{template.name}</h3>
                <p>{template.description}</p>
              </div>
              <div className="template-sections">
                {template.sections.map((section) => (
                  <small
                    key={section.id}
                    style={{ borderColor: section.color ?? undefined }}
                  >
                    {section.title}
                  </small>
                ))}
              </div>
              {template.teamId ? (
                <button
                  className="text-button"
                  disabled={busy === template.id}
                  onClick={() =>
                    void setTemplateStatus(
                      template.id,
                      template.status === "active" ? "archived" : "active",
                    )
                  }
                  type="button"
                >
                  {template.status === "active" ? "Archive" : "Restore"}
                </button>
              ) : (
                <small className="locked-label">Managed by Taskei</small>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="admin-panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Custom format</p>
            <h2>Create a template</h2>
          </div>
        </div>
        <form className="template-form" onSubmit={createTemplate}>
          <div className="template-basics">
            <label>
              <span>Template name<RequiredMark /></span>
              <input name="name" placeholder="Team health check" required />
            </label>
            <label>
              Description
              <input
                name="description"
                placeholder="What conversation should this format support?"
              />
            </label>
          </div>
          <div className="section-builder">
            {Array.from({ length: sectionCount }, (_, index) => (
              <div key={index}>
                <span style={{ background: `var(--template-color-${index + 1})` }}>
                  {index + 1}
                </span>
                <label>
                  <span>Section title<RequiredMark /></span>
                  <input
                    name={`sectionTitle${index}`}
                    placeholder={["Working", "Worried", "Wishing"][index] || "Section"}
                    required
                  />
                </label>
                <label>
                  Prompt
                  <input
                    name={`sectionPrompt${index}`}
                    placeholder="Question shown to participants"
                  />
                </label>
              </div>
            ))}
          </div>
          <div className="template-form-actions">
            <button
              className="button secondary"
              disabled={sectionCount >= 6}
              onClick={() => setSectionCount((count) => Math.min(6, count + 1))}
              type="button"
            >
              + Add section
            </button>
            <button
              className="button secondary"
              disabled={sectionCount <= 2}
              onClick={() => setSectionCount((count) => Math.max(2, count - 1))}
              type="button"
            >
              Remove last
            </button>
            <button className="button primary" disabled={busy === "template"} type="submit">
              {busy === "template" ? "Creating…" : "Create template"}
            </button>
          </div>
        </form>
      </section>
    </section>
  );
}

function NewTeamDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (teamId: string) => Promise<void>;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ id: string }>("/api/teams", {
        method: "POST",
        body: JSON.stringify({ name: form.get("name") }),
      });
      await onCreated(result.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create team.");
      setBusy(false);
    }
  };
  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section
        aria-modal="true"
        className="dialog"
        onMouseDown={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
      >
        <button className="dialog-close" onClick={onClose} type="button">×</button>
        <p className="eyebrow">New workspace</p>
        <h2>Create a team</h2>
        <p>Start a separate retrospective history and membership list.</p>
        <form onSubmit={submit}>
          <label>
            <span>Team name<RequiredMark /></span>
            <input autoFocus name="name" placeholder="Product Engineering" required />
          </label>
          <ErrorBanner>{error}</ErrorBanner>
          <div className="dialog-actions">
            <button className="button secondary" onClick={onClose} type="button">Cancel</button>
            <button className="button primary" disabled={busy} type="submit">
              {busy ? "Creating…" : "Create team"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Portal({ user }: { user: User }) {
  const confirm = useConfirm();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [route, setRoute] = useState<PortalRoute>(() => readPortalRoute());
  const [detail, setDetail] = useState<RetroDetail | null>(null);
  const [newRetro, setNewRetro] = useState(false);
  const [newTeam, setNewTeam] = useState(false);
  const [error, setError] = useState("");
  const [sprintFilter, setSprintFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const navigate = useCallback((next: PortalRoute, replace = false) => {
    const nextPath = portalRoutePath(next);
    window.history[replace ? "replaceState" : "pushState"]({}, "", nextPath);
    setRoute(next);
  }, []);

  const navigateToView = useCallback(
    (view: PortalView, teamId = workspace?.team.id ?? route.teamId) => {
      navigate({
        retrospectiveId: null,
        sprintId: null,
        taskId: null,
        taskMode: view === "backlog" ? "backlog" : view === "scrum" ? "scrum" : "board",
        teamId,
        view,
      });
    },
    [navigate, route.teamId, workspace?.team.id],
  );

  const loadWorkspace = useCallback(async (teamId?: string) => {
    const selectedTeam =
      teamId ?? readPortalRoute().teamId ?? window.localStorage.getItem("taskei-team") ?? "";
    const result = await api<Workspace>(
      `/api/workspace${selectedTeam ? `?teamId=${encodeURIComponent(selectedTeam)}` : ""}`,
    );
    window.localStorage.setItem("taskei-team", result.team.id);
    setWorkspace(result);
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    const result = await api<RetroDetail>(`/api/retrospectives/${id}`);
    setDetail(result);
  }, []);

  useEffect(() => {
    loadWorkspace().catch((reason) =>
      setError(reason instanceof Error ? reason.message : "Could not load workspace."),
    );
  }, [loadWorkspace]);

  useEffect(() => {
    const onPopState = () => setRoute(readPortalRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (workspace && !route.teamId) {
      navigate({ ...route, teamId: workspace.team.id }, true);
    }
  }, [navigate, route, workspace]);

  useEffect(() => {
    if (workspace && route.teamId && route.teamId !== workspace.team.id) {
      setError("");
      loadWorkspace(route.teamId).catch((reason) =>
        setError(reason instanceof Error ? reason.message : "Could not load workspace."),
      );
    }
  }, [loadWorkspace, route.teamId, workspace]);

  useEffect(() => {
    if (route.retrospectiveId) {
      setDetail(null);
      loadDetail(route.retrospectiveId).catch((reason) =>
        setError(reason instanceof Error ? reason.message : "Could not load retrospective."),
      );
    } else {
      setDetail(null);
    }
  }, [loadDetail, route.retrospectiveId]);

  const updateAction = async (id: string, status: ActionItem["status"]) => {
    await api(`/api/work-items/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    await loadWorkspace();
  };

  const deleteWorkspaceAction = async (action: ActionItem) => {
    const ok = await confirm({ title: `Permanently delete the action "${action.title}"?`, tone: "danger" });
    if (!ok) return;
    await api(`/api/work-items/${action.id}`, { method: "DELETE" });
    await loadWorkspace();
  };

  const pushWorkspaceActionToBacklog = async (action: ActionItem, sprintId: string | null) => {
    await api(`/api/work-items/${action.id}/push-to-backlog`, {
      body: JSON.stringify({ sprintId }),
      method: "POST",
    });
    await loadWorkspace();
  };

  const refreshDetail = useCallback(async () => {
    if (!route.retrospectiveId) return;
    await Promise.all([loadDetail(route.retrospectiveId), loadWorkspace()]);
  }, [loadDetail, loadWorkspace, route.retrospectiveId]);

  const filteredRetrospectives = useMemo(
    () =>
      workspace?.retrospectives.filter((retro) => {
        const sprint = `${retro.sprintLabel ?? ""} ${retro.title}`.toLowerCase();
        return (
          (!sprintFilter || sprint.includes(sprintFilter.toLowerCase())) &&
          (!stageFilter || retro.stage === stageFilter) &&
          (!dateFilter || retro.createdAt.slice(0, 10) >= dateFilter)
        );
      }) ?? [],
    [dateFilter, sprintFilter, stageFilter, workspace?.retrospectives],
  );
  const filteredActions = useMemo(
    () =>
      workspace?.actions.filter(
        (action) =>
          (!ownerFilter || action.ownerId === ownerFilter) &&
          (!dateFilter || (action.dueOn ?? "9999-12-31") >= dateFilter),
      ) ?? [],
    [dateFilter, ownerFilter, workspace?.actions],
  );

  if (!workspace) {
    return (
      <div className="app-loading">
        <LogoMark />
        <p>{error || "Preparing your Taskei workspace…"}</p>
      </div>
    );
  }

  if (route.retrospectiveId && detail) {
    return (
      <div className="app-shell bootstrap-shell" data-ui-theme="jira">
        <AppChrome
          actionCount={workspace.actions.length}
          isAdmin={workspace.team.role === "admin"}
          onCreateTeam={() => {
            setNewTeam(true);
          }}
          onNavigate={navigateToView}
          onTeamChange={async (teamId) => {
            setDetail(null);
            navigateToView("retros", teamId);
            await loadWorkspace(teamId);
          }}
          team={workspace.team}
          teams={workspace.teams}
          user={user}
          view="retros"
        />
        <main className="app-content">
          <RetroBoard
            canAdmin={workspace.team.role === "admin"}
            currentUserId={user.id}
            detail={detail}
            onDeleted={async () => {
              setDetail(null);
              navigateToView("retros");
              await loadWorkspace();
            }}
            onBack={() => navigateToView("retros")}
            onChanged={refreshDetail}
            onOpenTask={(taskId) => navigate({
              retrospectiveId: null, sprintId: null, taskId,
              taskMode: "board", teamId: workspace.team.id, view: "board",
            })}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell bootstrap-shell" data-ui-theme="jira">
      <AppChrome
        actionCount={workspace.actions.length}
        isAdmin={workspace.team.role === "admin"}
        onCreateTeam={() => setNewTeam(true)}
        onNavigate={navigateToView}
        onTeamChange={async (teamId) => {
          navigateToView("retros", teamId);
          await loadWorkspace(teamId);
        }}
        team={workspace.team}
        teams={workspace.teams}
        user={user}
        view={route.view}
      />
      <main className="app-content">
        <header className="app-topbar">
          <div>
            <p>{workspace.team.name}</p>
            <h1>
              {route.view === "retros"
                ? "Retrospectives"
                : route.view === "actions"
                  ? "Action items"
                  : route.view === "board"
                    ? "Board"
                    : route.view === "backlog"
                      ? "Backlog"
                      : route.view === "hierarchy"
                        ? "Hierarchy"
                        : route.view === "sprints"
                          ? "Sprints"
                          : route.view === "goals"
                            ? "Goals"
                          : route.view === "reports"
                            ? "Reports"
                          : route.view === "imports"
                            ? "Imports"
                          : route.view === "scrum"
                            ? "Daily scrum"
                          : route.view === "onboarding"
                            ? "Onboarding"
                            : "Administration"}
            </h1>
          </div>
          {route.view === "retros" && (
            <button className="button primary" onClick={() => setNewRetro(true)} type="button">
              + New retrospective
            </button>
          )}
        </header>
        <ErrorBanner>{error}</ErrorBanner>
        {route.view === "retros" ? (
          <section>
            <div className="dashboard-stats">
              <article>
                <span>Retrospectives</span>
                <strong>{workspace.metrics.totalRetrospectives}</strong>
              </article>
              <article>
                <span>Actions completed</span>
                <strong>{workspace.metrics.actionCompletionRate}%</strong>
              </article>
              <article>
                <span>Closed retrospectives</span>
                <strong>{workspace.metrics.closedRetrospectives}</strong>
              </article>
              <article>
                <span>Overdue actions</span>
                <strong className={workspace.metrics.overdueActions ? "metric-alert" : ""}>
                  {workspace.metrics.overdueActions}
                </strong>
              </article>
            </div>
            <div className="filter-bar">
              <label>
                Sprint
                <input
                  onChange={(event) => setSprintFilter(event.target.value)}
                  placeholder="Search sprint or title"
                  value={sprintFilter}
                />
              </label>
              <label>
                Stage
                <select onChange={(event) => setStageFilter(event.target.value)} value={stageFilter}>
                  <option value="">All stages</option>
                  <option value="draft">Draft</option>
                  <option value="collecting">Collecting</option>
                  <option value="discussing">Discussing</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
              <label>
                Since
                <input onChange={(event) => setDateFilter(event.target.value)} type="date" value={dateFilter} />
              </label>
            </div>
            <div className="section-heading">
              <div>
                <p className="eyebrow">Team history</p>
                <h2>Recent retrospectives</h2>
              </div>
            </div>
            {filteredRetrospectives.length ? (
              <div className="retro-list">
                {filteredRetrospectives.map((retro) => (
                  <button
                    className="retro-list-item"
                    key={retro.id}
                    onClick={() => navigate({ ...route, retrospectiveId: retro.id })}
                    type="button"
                  >
                    <span className="retro-icon">
                      {templateInitials(retro.templateName || "Retro")}
                    </span>
                    <span>
                      <strong>{retro.title}</strong>
                      <small>
                        {retro.sprintLabel || "Sprint"} · {retro.templateName || "Template"} ·
                        Facilitated by {retro.facilitatorName}
                      </small>
                    </span>
                    <span className="retro-metrics">
                      {retro.cardCount} notes · {retro.completedActionCount}/{retro.actionCount} actions
                      {retro.overdueActionCount ? ` · ${retro.overdueActionCount} overdue` : ""}
                    </span>
                    <span className="stage-pill">{retro.stage}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <span className="empty-mark">4L</span>
                <h2>Run your first team retrospective</h2>
                <p>
                  Start with the 4Ls format, collect reflections, and assign every
                  follow-up to an owner.
                </p>
                <button className="button primary" onClick={() => setNewRetro(true)} type="button">
                  Create retrospective
                </button>
              </div>
            )}
          </section>
        ) : route.view === "actions" ? (
          <section>
            <div className="section-heading">
              <div>
                <p className="eyebrow">Across all retrospectives</p>
                <h2>Pending team actions</h2>
              </div>
            </div>
            <div className="filter-bar">
              <label>
                Owner
                <select onChange={(event) => setOwnerFilter(event.target.value)} value={ownerFilter}>
                  <option value="">All owners</option>
                  {workspace.members.map((member) => (
                    <option key={member.id} value={member.id}>{member.displayName}</option>
                  ))}
                </select>
              </label>
              <label>
                Due on or after
                <input onChange={(event) => setDateFilter(event.target.value)} type="date" value={dateFilter} />
              </label>
            </div>
            <div className="action-list large">
              {filteredActions.length ? (
                filteredActions.map((action) => (
                  <ActionRow
                    action={action}
                    canChangeStatus={
                      workspace.team.role === "admin"
                        || action.ownerId === user.id
                        || action.facilitatorId === user.id
                    }
                    key={action.id}
                    members={workspace.members}
                    onDelete={
                      workspace.team.role === "admin"
                        ? deleteWorkspaceAction
                        : undefined
                    }
                    onOpenTask={(taskId) => navigate({
                      retrospectiveId: null, sprintId: null, taskId,
                      taskMode: "board", teamId: workspace.team.id, view: "board",
                    })}
                    onPushToBacklog={pushWorkspaceActionToBacklog}
                    onStatus={updateAction}
                  />
                ))
              ) : (
                <div className="empty-state compact">
                  <h2>Everything is complete</h2>
                  <p>New action items assigned during retrospectives will appear here.</p>
                </div>
              )}
            </div>
          </section>
        ) : ["board", "backlog", "scrum"].includes(route.view) ? (
          <TaskWorkspace
            currentUserId={user.id}
            initialQuery={route.query}
            isAdmin={workspace.team.role === "admin"}
            members={workspace.members}
            mode={route.view === "backlog" ? "backlog" : route.view === "scrum" ? "scrum" : "board"}
            onNavigate={(taskRoute) =>
              navigate({
                ...route,
                retrospectiveId: null,
                query: taskRoute.query ?? route.query,
                sprintId: taskRoute.sprintId ?? null,
                taskId: taskRoute.taskId ?? null,
                taskMode: taskRoute.mode,
                teamId: workspace.team.id,
                view: taskRoute.mode === "backlog" ? "backlog" : taskRoute.mode === "scrum" ? "scrum" : "board",
              })
            }
            onOpenSprints={() => navigate({ ...route, view: "sprints" })}
            onQueryChange={(query) => navigate({ ...route, query }, true)}
            sprintId={route.sprintId}
            taskId={route.taskId}
            teamId={workspace.team.id}
          />
        ) : route.view === "hierarchy" ? (
          <TaskHierarchy initialQuery={route.query} isAdmin={workspace.team.role === "admin"} onQueryChange={(query) => navigate({ ...route, query }, true)} teamId={workspace.team.id} />
        ) : route.view === "sprints" ? (
          <SprintsPage
            isAdmin={workspace.team.role === "admin"}
            onSelectSprint={(sprintId) => navigate({ ...route, sprintId })}
            selectedSprintId={route.sprintId}
            teamId={workspace.team.id}
          />
        ) : route.view === "goals" ? (
          <GoalsPage isAdmin={workspace.team.role === "admin"} members={workspace.members} teamId={workspace.team.id} />
        ) : route.view === "reports" ? (
          <ReportsPage
            onSelectSprint={(sprintId) => navigate({ ...route, sprintId })}
            sprintId={route.sprintId}
            teamId={workspace.team.id}
          />
        ) : route.view === "imports" ? (
          <ImportsWorkspace members={workspace.members} onImported={loadWorkspace} teamId={workspace.team.id} templates={workspace.templates} />
        ) : route.view === "onboarding" ? (
          <OnboardingPage members={workspace.members} teamId={workspace.team.id} teams={workspace.teams} userId={user.id} />
        ) : (
          <Administration
            onWorkspaceChanged={loadWorkspace}
            onTeamArchived={async () => {
              window.localStorage.removeItem("taskei-team");
              navigateToView("retros", null);
              await loadWorkspace("");
            }}
            team={workspace.team}
            user={user}
          />
        )}
      </main>
      {newRetro && (
        <NewRetroDialog
          team={workspace.team}
          templates={workspace.templates}
          onClose={() => setNewRetro(false)}
          onCreated={async (id) => {
            setNewRetro(false);
            await loadWorkspace();
            navigate({ ...route, retrospectiveId: id, teamId: workspace.team.id, view: "retros" });
          }}
        />
      )}
      {newTeam && (
        <NewTeamDialog
          onClose={() => setNewTeam(false)}
          onCreated={async (teamId) => {
            setNewTeam(false);
            navigateToView("retros", teamId);
            await loadWorkspace(teamId);
          }}
        />
      )}
    </div>
  );
}

function AppChrome({
  actionCount,
  isAdmin,
  onCreateTeam,
  onNavigate,
  onTeamChange,
  team,
  teams,
  user,
  view,
}: {
  actionCount: number;
  isAdmin: boolean;
  onCreateTeam: () => void;
  onNavigate: (view: PortalView) => void;
  onTeamChange: (teamId: string) => Promise<void>;
  team: Team;
  teams: Team[];
  user: User;
  view: PortalView;
}) {
  type SearchResult = { assigneeName: string; id: string; status: string; taskKey: string; teamId: string; teamName: string; title: string; type: string; url: string };
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchRevision, setSearchRevision] = useState(0);
  const [agentTokensOpen, setAgentTokensOpen] = useState(false);
  const [activeSearchResult, setActiveSearchResult] = useState(0);
  const searchInput = useRef<HTMLInputElement>(null);
  const searchSequence = useRef(0);
  const navigation: Array<{ icon: string; label: string; view: PortalView }> = [
    { icon: "bi-kanban", label: "Board", view: "board" },
    { icon: "bi-list-task", label: "Backlog", view: "backlog" },
    { icon: "bi-calendar3", label: "Sprints", view: "sprints" },
    { icon: "bi-flag", label: "Goals", view: "goals" },
    ...((isAdmin || team.taskPermissions.includes("view_task_reports"))
      ? [{ icon: "bi-graph-down", label: "Reports", view: "reports" as PortalView }]
      : []),
    { icon: "bi-people", label: "Daily scrum", view: "scrum" },
    { icon: "bi-diagram-3", label: "Hierarchy", view: "hierarchy" },
    { icon: "bi-arrow-repeat", label: "Retrospectives", view: "retros" },
    { icon: "bi-check2-square", label: "Action items", view: "actions" },
    { icon: "bi-mortarboard", label: "Onboarding", view: "onboarding" },
    ...((isAdmin || team.taskPermissions.includes("manage_integrations"))
      ? [{ icon: "bi-cloud-arrow-down", label: "Imports", view: "imports" as PortalView }]
      : []),
    ...(isAdmin ? [{ icon: "bi-gear", label: "Administration", view: "admin" as PortalView }] : []),
  ];

  const openTaskComposer = () => {
    if (!["board", "backlog"].includes(view)) onNavigate("board");
    window.setTimeout(() => window.dispatchEvent(new CustomEvent("taskei:create-task")), 0);
  };

  useEffect(() => {
    const focusSearch = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select, [contenteditable=true]")) return;
      event.preventDefault(); searchInput.current?.focus(); setSearchOpen(true);
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);

  useEffect(() => {
    const current = ++searchSequence.current;
    if (searchQuery.trim().length < 2) {
      setSearchResults([]); setSearchBusy(false); setSearchError("");
      return;
    }
    setSearchBusy(true); setSearchError("");
    const timeout = window.setTimeout(() => {
      api<{ hasMore: boolean; results: SearchResult[] }>(`/api/tasks/search?query=${encodeURIComponent(searchQuery.trim())}`)
        .then(({ hasMore, results }) => {
          if (current !== searchSequence.current) return;
          setSearchResults(results); setSearchHasMore(hasMore); setActiveSearchResult(0); setSearchBusy(false);
        })
        .catch((reason) => {
          if (current !== searchSequence.current) return;
          setSearchBusy(false); setSearchError(reason instanceof Error ? reason.message : "Task search is unavailable.");
        });
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchQuery, searchRevision]);

  const loadMoreSearchResults = async () => {
    setSearchBusy(true);
    try {
      const result = await api<{ hasMore: boolean; results: SearchResult[] }>(`/api/tasks/search?query=${encodeURIComponent(searchQuery.trim())}&offset=${searchResults.length}`);
      setSearchResults((value) => [...value, ...result.results]); setSearchHasMore(result.hasMore);
    } catch (reason) { setSearchError(reason instanceof Error ? reason.message : "Task search is unavailable."); }
    finally { setSearchBusy(false); }
  };
  const groupedSearchResults = useMemo(() => {
    const groups = new Map<string, SearchResult[]>();
    for (const result of searchResults) groups.set(result.teamName, [...(groups.get(result.teamName) ?? []), result]);
    return [...groups.entries()];
  }, [searchResults]);

  const searchTasks = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const selected = searchResults[activeSearchResult];
    if (selected) window.location.assign(selected.url);
  };

  const searchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") { setSearchOpen(false); searchInput.current?.blur(); return; }
    if (event.key === "ArrowDown") { event.preventDefault(); setActiveSearchResult((value) => Math.min(searchResults.length - 1, value + 1)); }
    if (event.key === "ArrowUp") { event.preventDefault(); setActiveSearchResult((value) => Math.max(0, value - 1)); }
  };

  return (
    <>
    <header className="app-chrome">
      <div className="global-topbar">
        <a aria-label="Taskei home" className="chrome-brand" href="/">
          <LogoMark />
          <strong>Taskei</strong>
        </a>
        <label className="chrome-team-switcher">
          <span className="sr-only">Team</span>
          <select
            aria-label="Current team"
            onChange={(event) => void onTeamChange(event.target.value)}
            value={team.id}
          >
            {teams.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>{candidate.name}</option>
            ))}
          </select>
        </label>
        <form className="chrome-search" onSubmit={searchTasks}>
          <i aria-hidden="true" className="bi bi-search" />
          <input aria-autocomplete="list" aria-controls="global-task-results" aria-expanded={searchOpen} aria-label="Search all tasks" autoComplete="off" name="query" onChange={(event) => { setSearchQuery(event.target.value); setSearchOpen(true); }} onFocus={() => setSearchOpen(true)} onKeyDown={searchKeyDown} placeholder="Search tasks" ref={searchInput} value={searchQuery} />
          <kbd>/</kbd>
          {searchOpen && searchQuery.trim().length >= 2 && <div aria-label="Task search results" className="global-search-results" id="global-task-results" role="listbox">
            {searchBusy ? <p>Searching…</p> : searchError ? <div role="alert"><p>{searchError}</p><button onClick={() => setSearchRevision((value) => value + 1)} type="button">Retry</button></div> : searchResults.length ? groupedSearchResults.map(([teamName, results]) => <section aria-label={teamName} className="global-search-group" key={teamName} role="group"><h3>{teamName}</h3>{results.map((result) => { const index = searchResults.indexOf(result); return <button aria-selected={index === activeSearchResult} className={index === activeSearchResult ? "active" : ""} key={result.id} onMouseDown={(event) => event.preventDefault()} onClick={() => window.location.assign(result.url)} onMouseEnter={() => setActiveSearchResult(index)} role="option" type="button"><span><strong>{result.taskKey}</strong><small>{result.teamName}</small></span><span>{result.title}<small>{result.type} · {result.status.replace("_", " ")} · {result.assigneeName}</small></span></button>; })}</section>) : <p>No tasks found.</p>}
            {searchHasMore && !searchBusy && <button className="global-search-more" onClick={() => void loadMoreSearchResults()} type="button">Load more results</button>}
          </div>}
        </form>
        <button className="chrome-create" onClick={openTaskComposer} type="button">Create</button>
        <span className="chrome-spacer" />
        <NotificationBell />
        <button
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          className="chrome-icon-button"
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          type="button"
        >
          <i aria-hidden="true" className={`bi ${theme === "dark" ? "bi-sun" : "bi-moon-stars"}`} />
        </button>
        <button aria-label="Create a team" className="chrome-icon-button" onClick={onCreateTeam} title="Create a team" type="button"><i aria-hidden="true" className="bi bi-people" /></button>
        <button aria-label="Agent access" className="chrome-icon-button" onClick={() => setAgentTokensOpen(true)} title="Agent access" type="button"><i aria-hidden="true" className="bi bi-robot" /></button>
        <div className="chrome-user">
          <Avatar user={user} />
          <span>{user.displayName}</span>
        </div>
        <form action="/auth/logout" method="post">
          <button aria-label="Sign out" className="chrome-icon-button" title="Sign out" type="submit"><i aria-hidden="true" className="bi bi-box-arrow-right" /></button>
        </form>
      </div>
      <div className="workspace-bar">
        <div className="workspace-title"><span>Workspace</span><strong>{team.name}</strong></div>
        <nav aria-label="Workspace navigation" className="workspace-nav">
          {navigation.map((item) => <button aria-current={view === item.view ? "page" : undefined} className={view === item.view ? "active" : ""} key={item.view} onClick={() => onNavigate(item.view)} type="button"><i aria-hidden="true" className={`bi ${item.icon}`} /><span>{item.label}</span>{item.view === "actions" && actionCount > 0 && <small>{actionCount}</small>}</button>)}
        </nav>
      </div>
    </header>
    {agentTokensOpen && <AgentTokensDialog onClose={() => setAgentTokensOpen(false)} />}
    </>
  );
}

// Reuses /api/tasks/search (already membership-scoped and already ranks an
// exact task_key match first) rather than adding a new lookup endpoint.
function SharedTaskRedirect({ taskKey }: { taskKey: string }) {
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api<{ results: Array<{ taskKey: string; url: string }> }>(
      `/api/tasks/search?query=${encodeURIComponent(taskKey)}`,
    )
      .then(({ results }) => {
        if (cancelled) return;
        const match = results.find((candidate) => candidate.taskKey.toLowerCase() === taskKey.toLowerCase());
        if (match) { window.location.replace(match.url); return; }
        setError(`No task found for ${taskKey}. It may not exist, or you may not have access to its team.`);
      })
      .catch((reason) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not look up that task.");
      });
    return () => { cancelled = true; };
  }, [taskKey]);

  return (
    <main className="landing">
      <nav className="landing-nav">
        <a className="brand" href="/">
          <LogoMark />
          <span>Taskei</span>
        </a>
      </nav>
      <div className="empty-state" style={{ margin: "4rem auto", maxWidth: 480 }}>
        {error ? (
          <>
            <h2>Task not found</h2>
            <p>{error}</p>
            <a className="button primary" href="/">Return to Taskei Portal</a>
          </>
        ) : (
          <>
            <h2>Opening {taskKey}…</h2>
            <p>Looking up this task.</p>
          </>
        )}
      </div>
    </main>
  );
}

function App() {
  const [config, setConfig] = useState<PortalConfig | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sharedTaskKey = useMemo(() => readSharedTaskKey(), []);

  useEffect(() => {
    Promise.all([
      api<PortalConfig>("/api/config"),
      api<Session>("/api/session"),
    ])
      .then(([nextConfig, nextSession]) => {
        setConfig(nextConfig);
        setSession(nextSession);
      })
      .catch((reason) =>
        setError(reason instanceof Error ? reason.message : "Taskei is unavailable."),
      );
  }, []);

  if (sharedTaskKey && !session?.authenticated) {
    return <Landing config={config} error={error} returnTo={window.location.pathname} />;
  }
  if (sharedTaskKey) {
    return <SharedTaskRedirect taskKey={sharedTaskKey} />;
  }
  if (session?.authenticated) {
    return <Portal user={session.user} />;
  }
  return <Landing config={config} error={error} />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <ToastProvider>
        <ConfirmProvider>
          <App />
        </ConfirmProvider>
      </ToastProvider>
    </ErrorBoundary>
  </StrictMode>,
);
